/**
 * Web task rows — the twin of `task-rows.tsx`.
 *
 * Each row: a status badge (a ring with the step number while pending or
 * running, a green check or red cross once resolved), the label, an amount in
 * tabular figures, a status pill, and a chevron that drops down detail lines
 * beside a hairline rail. Two layouts: `capsules` gives every row its own
 * tinted surface (radius 22, easing to 14 when open); `list` stacks them in
 * one surface with dividers, for use inside a tool card.
 *
 * Surfaces are flat — tint only, no shadow — per the chat card rules.
 */
import { useState, type CSSProperties, type FC, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { defaultTaskRowsLabels, type TaskRowItem, type TaskRowsLabels } from './task-rows-model';

const RING_SIZE = 24;
const RING_STROKE = 2;

export const TaskSpinnerRing: FC<{ active?: boolean; children?: ReactNode }> = ({ active, children }) => {
  const r = (RING_SIZE - RING_STROKE) / 2;
  const c = 2 * Math.PI * r;
  return (
    <span className={`tabbio-task-ring${active ? ' is-active' : ''}`}>
      <svg width={RING_SIZE} height={RING_SIZE} aria-hidden>
        <circle cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={r} fill="none" stroke="var(--input-border)" strokeWidth={RING_STROKE} />
        {active ? (
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={r}
            fill="none"
            stroke="var(--text-tertiary)"
            strokeWidth={RING_STROKE}
            strokeLinecap="round"
            strokeDasharray={`${c * 0.28} ${c * 0.72}`}
          />
        ) : null}
      </svg>
      <span className="tabbio-task-ring-step">{children}</span>
    </span>
  );
};

const CheckIcon = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20 6L9 17l-5-5" />
  </svg>
);
const XIcon = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" aria-hidden>
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);
const RetryIcon = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M21 12a9 9 0 1 1-2.64-6.36M21 3v6h-6" />
  </svg>
);

export const TaskBadge: FC<{ tone: 'done' | 'failed' }> = ({ tone }) => (
  <span className={`tabbio-task-badge is-${tone}`}>{tone === 'done' ? CheckIcon : XIcon}</span>
);

function badgeFor(row: TaskRowItem) {
  switch (row.status) {
    case 'done':
      return <TaskBadge tone="done" />;
    case 'failed':
      return <TaskBadge tone="failed" />;
    case 'running':
      return <TaskSpinnerRing active>{row.step}</TaskSpinnerRing>;
    default:
      return <TaskSpinnerRing>{row.step}</TaskSpinnerRing>;
  }
}

function pillFor(row: TaskRowItem, labels: TaskRowsLabels) {
  switch (row.status) {
    case 'done':
      return <span className="tabbio-task-pill is-done">{row.statusLabel ?? labels.completed}</span>;
    case 'failed':
      return (
        <span className="tabbio-task-pill is-failed">
          {row.statusLabel ?? labels.failed}
          <span className="tabbio-task-pill-retry">{RetryIcon}</span>
        </span>
      );
    case 'pending':
      return row.statusLabel ? <span className="tabbio-task-pill is-pending">{row.statusLabel}</span> : null;
    default:
      return null;
  }
}

export const TaskRows: FC<{
  rows: TaskRowItem[];
  variant?: 'capsules' | 'list';
  labels?: Partial<TaskRowsLabels>;
  className?: string;
  onToggleRow?: (key: string, open: boolean) => void;
}> = ({ rows, variant = 'capsules', labels, className, onToggleRow }) => {
  const { t } = useTranslation('common');
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({});
  const copy: TaskRowsLabels = { ...defaultTaskRowsLabels(t), ...labels };

  return (
    <div className={`tabbio-tasks is-${variant}${className ? ` ${className}` : ''}`}>
      {rows.map((row, index) => {
        const open = openRows[row.key] ?? false;
        const canOpen = row.details.length > 0;
        return (
          <div
            key={row.key}
            className={`tabbio-task${open ? ' is-open' : ''}`}
            style={{ ['--row-delay' as string]: `${index * 80}ms` } as CSSProperties}
          >
            <button
              type="button"
              className="tabbio-task-head"
              aria-expanded={canOpen ? open : undefined}
              disabled={!canOpen}
              onClick={() => {
                if (!canOpen) return;
                setOpenRows((current) => ({ ...current, [row.key]: !open }));
                onToggleRow?.(row.key, !open);
              }}
            >
              <span className="tabbio-task-badge-slot">{badgeFor(row)}</span>
              <span className="tabbio-task-label" dir="auto">
                {row.label}
              </span>
              {row.amount ? (
                <span className="tabbio-task-amount" dir="auto">
                  {row.amount}
                </span>
              ) : null}
              {pillFor(row, copy)}
              {canOpen ? (
                <span className="tabbio-task-chevron" aria-hidden>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </span>
              ) : null}
            </button>

            {canOpen ? (
              <div className="tabbio-task-body" aria-hidden={!open}>
                <div>
                  <div className="tabbio-task-details">
                    <span aria-hidden className="tabbio-task-rail" />
                    <div className="tabbio-task-detail-list">
                      {row.details.map((detail, detailIndex) => (
                        <div
                          key={`${detail.label}-${detailIndex}`}
                          className="tabbio-task-detail"
                          style={{ ['--row-delay' as string]: `${120 + detailIndex * 100}ms` } as CSSProperties}
                        >
                          <span className="tabbio-task-detail-label" dir="auto">
                            {detail.label}
                          </span>
                          {detail.meta ? (
                            <span className="tabbio-task-detail-meta" dir="auto">
                              {detail.meta}
                            </span>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export default TaskRows;
